import {
  IssuedTicketWithTravellerDTO,
  TransactionWithProductsDTO,
  useGetTravellerByID,
  useGetBearersForIssuedTicket,
  useGetOrganizationByID,
} from '@/api/trafikportalen';
import { useMemo } from 'react';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import Button from '../core/Button';
import { useImpersonation } from '@/hooks/useImpersonation';
import IconButton from '../core/IconButton';
import { IconArrowRight, IconTicket } from '@/icons';
import { Route } from '@/routes/$tenantId/_layout/reports/transactions/$transactionId';
import { FormattedMessage, useIntl } from 'react-intl';
import { OriginalBearerInfo } from '@/components/OriginalBearerInfo';

export const IssuedTicket = ({
  issuedTicket,
  transaction,
}: {
  issuedTicket: IssuedTicketWithTravellerDTO;
  transaction: TransactionWithProductsDTO;
}) => {
  const params = Route.useParams();
  const impersonate = useImpersonation();
  const { formatMessage } = useIntl();
  const { traveller_first_name, traveller_last_name, transaction_ticket } =
    issuedTicket;
  const { data: organization } = useGetOrganizationByID(
    transaction.transaction.organization_id,
    {
      query: { enabled: checkUserPermissions({ permission: 'tenant:viewer' }) },
    },
  );

  const { data: traveller } = useGetTravellerByID(
    issuedTicket.traveller_id ?? '',
    {
      query: {
        enabled:
          !!issuedTicket.traveller_id &&
          !traveller_first_name &&
          !traveller_last_name &&
          !checkUserPermissions({ permission: 'tenant:viewer' }),
      },
    },
  );

  const { data: ticketBearers } = useGetBearersForIssuedTicket(
    issuedTicket.ticket.id,
    {
      query: {
        enabled: !checkUserPermissions({ permission: 'tenant:viewer' }),
      },
    },
  );

  const travellerName = useMemo(() => {
    if (traveller_first_name || traveller_last_name) {
      return `${traveller_first_name || ''}${traveller_last_name ? ` ${traveller_last_name}` : ''}`;
    }
    if (traveller) {
      return traveller.traveller.email;
    }
    return formatMessage({ defaultMessage: 'Anonym resenär' });
  }, [traveller_first_name, traveller_last_name, traveller, formatMessage]);

  if (checkUserPermissions({ permission: 'tenant:viewer' })) {
    if (organization?.state !== 'approved') {
      return <p className="w-fit truncate">{travellerName}</p>;
    }

    return (
      <div className="flex w-full flex-col">
        {issuedTicket.ticket.recipient_name ? (
          // The pr-1 is to prevent the text from being cut off
          <p className="w-fit truncate pr-1">
            {issuedTicket.ticket.recipient_name}{' '}
            <i className="text-text-700">
              (<FormattedMessage defaultMessage="Tillfällig resenär" />)
            </i>
          </p>
        ) : (
          <Button
            key={`issued-ticket-${issuedTicket.ticket.id}`}
            size="sm"
            className="w-full justify-between"
            endIcon={<IconArrowRight aria-hidden="true" />}
            onClick={() => {
              impersonate(
                transaction.transaction.organization_id,
                '/$tenantId/travellers/$travellerId/$view',
                {
                  travellerId: issuedTicket.traveller_id ?? '',
                  view: 'profile',
                },
              );
            }}
          >
            <p className="min-w-0 truncate">{travellerName}</p>
          </Button>
        )}
        {transaction_ticket.original_bobcat_bearer_id !==
          ticketBearers?.[0]?.mtb_bearer_id && (
          <OriginalBearerInfo transactionTicket={transaction_ticket} />
        )}
      </div>
    );
  }

  return (
    <div
      key={`issued-ticket-${issuedTicket.ticket.id}`}
      className="flex w-full flex-col gap-0"
    >
      <div className="flex w-full items-center gap-1">
        <IconButton
          to="/$tenantId/tickets/$ticketId"
          params={{
            tenantId: params.tenantId,
            ticketId: issuedTicket.ticket.id ?? '',
          }}
          aria-label={formatMessage({ defaultMessage: 'Öppna biljett' })}
          title={formatMessage({ defaultMessage: 'Öppna biljett' })}
          icon={<IconTicket />}
          size="sm"
        />
        {issuedTicket.ticket.recipient_name ? (
          // The pr-1 is to prevent the text from being cut off
          <p className="w-fit truncate pr-1">
            {issuedTicket.ticket.recipient_name}{' '}
            <i className="text-text-700">
              (<FormattedMessage defaultMessage="Tillfällig resenär" />)
            </i>
          </p>
        ) : (
          <Button
            to="/$tenantId/travellers/$travellerId"
            params={{
              tenantId: params.tenantId,
              travellerId: issuedTicket.traveller_id ?? '',
            }}
            size="sm"
            className="w-full justify-between"
            endIcon={<IconArrowRight aria-hidden="true" />}
          >
            <p className="min-w-0 truncate">{travellerName}</p>
          </Button>
        )}
      </div>
      {transaction_ticket.original_bobcat_bearer_id !==
        ticketBearers?.[0]?.mtb_bearer_id && (
        <OriginalBearerInfo
          transactionTicket={transaction_ticket}
          indentClass="ml-10"
        />
      )}
    </div>
  );
};
